import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import classNames from 'classnames';
import { Link, useHistory } from 'react-router-dom';
import Markdown from 'react-markdown';

import { useFirebaseAuth } from '../../../../../global/FirebaseProvider/FirebaseProvider';
import { useSubscription } from '../../SubscriptionProvider';
import {
  createSubscriptionElementsIntent,
  finalizeSubscriptionElementsPayment,
  estimateSubscriptionTax,
  getSubscriptionEmailEligibility,
} from '../../../../utils/service';
import { getGenericError } from '../../../../utils/errors';
import CouponCodeInput from '../../../../global/CouponCodeInput';
import {
  SUBSCRIPTION_STEPS,
  getSubscriptionOrderItemsFromStorage,
  SUBSCRIPTION_PAYMENT_TYPES,
} from '../../../../utils/subscriptions';
import {
  SUBSCRIPTION_TRACKER_TYPES,
  TRACKER_IDS,
  getTrackerIdEventName,
} from '../../../../config/tracker';
import {
  ROUTE_HOME,
  SUBSCRIPTION_FROM_CHECKOUT_PARAM,
  STRIPE_PAYMENT_INTENT_ID_PARAM,
} from '../../../../config/routes';
import { MEMBERSHIP_TERMS_URL } from '../../../../config/externalURL';
import MEMBERSHIP_OFFERED_ITEMS, {
  getFilteredMembershipItems,
  MEMBERSHIP_DEFAULT_ORDER_ITEMS,
} from '../../../../utils/membership';
import { getUSStates } from '../../../../utils/locations';
import { isMobile, SIZE_XS, SIZE_SM, SIZE_MD } from '../../../../../../hooks/useBreakPoint';
import { pushSubscriptionStartedEvent } from '../../../../utils/gtm';
import { formatCurrencyNumber } from '../../../../utils/numbers';
import LoadingBar from '../../../../global/LoadingBar';
import trashieLogo from '../../../../images/trashie-logo.webp';
import mobileNavbarLogo from '../../../../images/subscription-navbar-mobile-logo.svg';
import mobileSummaryChevronIcon from '../../../../images/chevron-right-figma.svg';
import unlimitedBadgeFigmaIcon from '../../../../images/unlimited-badge-figma.svg';
import canCreateSubscriptionCheckoutSession from './checkoutSessionGate';
import SubscriptionUnlimitedElementsCheckout from './SubscriptionUnlimitedElementsCheckout';
import parsePlaceAddress from '../../../EnterAddress/placeAddressParser';
import { normalizeUsPhoneDigits, isValidUsPhone } from '../../../../utils/addresses';

import SubscriptionOrderSummary, { SubscriptionOrderSummaryLegal } from './SubscriptionOrderSummary';

import './SubscriptionUnlimitedCheckout.scss';

/** Maps relative terms links from subscription `info` markdown to the real terms URL */
function SubscriptionUnlimitedCheckoutTermsMarkdownLink({ href, children, ...rest }) {
  const url = href === 'terms' || href === '/terms' ? MEMBERSHIP_TERMS_URL : href;
  const isHttp = typeof url === 'string' && /^https?:/i.test(url);
  return (
    <a
      href={url}
      {...rest}
      {...(isHttp ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
    </a>
  );
}

const subscriptionUnlimitedCheckoutTermsMarkdownComponents = {
  a: SubscriptionUnlimitedCheckoutTermsMarkdownLink,
};

function formatUsPhoneDisplayFromDigits(digits10) {
  const d = String(digits10).replace(/\D/g, '').slice(0, 10);
  if (!d.length) {
    return '';
  }
  if (d.length <= 3) {
    return `+1 (${d}`;
  }
  if (d.length <= 6) {
    return `+1 (${d.slice(0, 3)}) ${d.slice(3)}`;
  }
  return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function getPhoneForApi(displayOrRaw) {
  const digits = normalizeUsPhoneDigits(
    displayOrRaw == null ? '' : String(displayOrRaw),
  );
  if (!digits) {
    return undefined;
  }
  return `+1${digits}`;
}

const CHECKOUT_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMAIL_SUBSCRIPTION_CONFLICT_MESSAGE =
  'An Unlimited membership is already active for this email. Sign in to manage your account.';

const EMAIL_ELIGIBILITY_DEBOUNCE_MS = 450;

/** Debounce create-elements-intent when coupon / refetches fire close together. */
const ELEMENTS_INTENT_DEBOUNCE_MS = 400;

const mapSubscriptionProductsForApi = (productsData) => {
  const membershipItems = getFilteredMembershipItems(SUBSCRIPTION_PAYMENT_TYPES.SUBSCRIPTION)
    .map((item) => item.id);

  return Object.entries(productsData)
    .filter(
      ([productType, quantity]) => membershipItems.includes(productType) && quantity > 0,
    )
    .map(([productType, quantity]) => ({
      productType: MEMBERSHIP_OFFERED_ITEMS[productType].id,
      quantity,
    }));
};

const {
  subscription: {
    payment: {
      [SUBSCRIPTION_TRACKER_TYPES.VIEW]: trackerView,
    },
  },
} = TRACKER_IDS;

const CouponTagIcon = () => (
  <svg
    className="SubscriptionUnlimitedCheckout__couponToggleIcon"
    width={24}
    height={24}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 6h.008v.008H6V6z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SubscriptionUnlimitedCheckout = () => {
  const history = useHistory();
  const hasTrackedInitiateCheckout = useRef(false);
  const hasTrackedSubscriptionStarted = useRef(false);
  const phoneHydratedFromProfileRef = useRef(false);
  const { userData, user: firebaseUser } = useFirebaseAuth();
  const {
    config: subscriptionConfig,
    subscriptionPlan,
    posthog,
    searchParams,
    setStep,
    setElementsPaymentResult,
  } = useSubscription();

  /* Same breakpoint as grid (992px): stacked vs row + sidebar */
  const isNarrowViewport = isMobile([SIZE_XS, SIZE_SM, SIZE_MD]);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [couponSectionOpen, setCouponSectionOpen] = useState(false);
  /** Figma 6462:23068 — contact + delivery + payment; default expanded */
  const [checkoutFormExpanded, setCheckoutFormExpanded] = useState(true);

  const [products] = useState(
    () => getSubscriptionOrderItemsFromStorage() || MEMBERSHIP_DEFAULT_ORDER_ITEMS,
  );
  const [checkoutEnabled, setCheckoutEnabled] = useState(true);
  const [errorStripe, setErrorStripe] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [isLoadingClientSecret, setIsLoadingClientSecret] = useState(true);
  const [couponMessage, setCouponMessage] = useState('');
  const [couponErrorMessage, setCouponErrorMessage] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCouponCode, setAppliedCouponCode] = useState('');
  const [discountedValue, setDiscountedValue] = useState(null);
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [checkoutEmail, setCheckoutEmail] = useState('');
  const [emailEligibilityError, setEmailEligibilityError] = useState('');
  const [checkoutFields, setCheckoutFields] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
  });
  const usStatesWithCodes = useMemo(
    () => getUSStates().filter((s) => s.code != null),
    [],
  );
  const addressAutocompleteRef = useRef(null);
  const checkoutEmailRef = useRef(checkoutEmail);
  const checkoutFieldsRef = useRef(checkoutFields);
  /** Invalidate in-flight create-elements-intent when prerequisites change or user edits address */
  const elementsIntentRequestGenRef = useRef(0);
  const expressPortalRef = useRef(null);
  const [cardholderName, setCardholderName] = useState('');
  const [emailMarketingOptIn, setEmailMarketingOptIn] = useState(false);
  const [smsMarketingOptIn, setSmsMarketingOptIn] = useState(false);
  const [billingCountryCode, setBillingCountryCode] = useState('US');
  const [estimatedTaxDollars, setEstimatedTaxDollars] = useState(null);
  const taxEstimateRequestRef = useRef(0);

  const {
    value: subscriptionValue,
    interval: subscriptionInterval = 'yr',
  } = subscriptionPlan || {};

  const membershipDisplayPrice = typeof subscriptionValue === 'number' && subscriptionValue >= 0
    ? subscriptionValue
    : 68;

  const canCreateCheckoutSession = canCreateSubscriptionCheckoutSession(products);

  const info = subscriptionConfig?.steps?.[SUBSCRIPTION_STEPS.PAYMENT]?.info || '';

  checkoutEmailRef.current = checkoutEmail;
  checkoutFieldsRef.current = checkoutFields;

  const fetchClientSecret = useCallback(async (
    productsData,
    couponId = null,
    requestGeneration = null,
  ) => {
    setIsLoadingClientSecret(true);
    setClientSecret(null);
    setCouponMessage('');
    setCouponErrorMessage('');

    const emailLive = checkoutEmailRef.current.trim();
    const fieldsLive = checkoutFieldsRef.current;

    const mappedProducts = mapSubscriptionProductsForApi(productsData);

    const {
      data,
      error: createCheckoutSessionError,
    } = await createSubscriptionElementsIntent({
      products: mappedProducts,
      couponId,
      email: emailLive || undefined,
      firstName: fieldsLive.firstName.trim() || undefined,
      lastName: fieldsLive.lastName.trim() || undefined,
      phone: getPhoneForApi(fieldsLive.phone),
      shippingAddress: {
        address1: fieldsLive.address1.trim() || undefined,
        address2: fieldsLive.address2.trim() || undefined,
        city: fieldsLive.city.trim() || undefined,
        provinceCode: fieldsLive.state.trim() || undefined,
        zip: fieldsLive.zip.trim() || undefined,
        countryCode: 'US',
      },
      searchParams,
    });

    if (
      requestGeneration != null &&
      requestGeneration !== elementsIntentRequestGenRef.current
    ) {
      setIsLoadingClientSecret(false);
      return;
    }

    if (createCheckoutSessionError || !data?.clientSecret) {
      setErrorStripe(getGenericError());
      setClientSecret(null);
      setCheckoutEnabled(false);
      setIsLoadingClientSecret(false);
      return;
    }

    const appliedCoupon = Boolean(couponId && data.couponApplied);

    setClientSecret(data?.clientSecret ?? null);
    setErrorStripe(null);
    setIsCouponApplied(Boolean(couponId && data.couponApplied));
    setDiscountedValue(data?.discountedValue ?? null);

    if (couponId) {
      if (appliedCoupon) {
        setCouponMessage('Coupon applied successfully');
        setCouponErrorMessage('');
      } else {
        setCouponMessage('');
        setCouponErrorMessage('Invalid coupon');
      }
    }

    setCouponCode('');
    setCheckoutEnabled(true);
    setIsLoadingClientSecret(false);
    if (!hasTrackedInitiateCheckout.current) {
      window.fbq?.('track', 'InitiateCheckout');
      hasTrackedInitiateCheckout.current = true;
    }
  }, [searchParams]);

  const hasAddressForTax = Boolean(
    checkoutFields.state?.trim() && checkoutFields.zip?.trim(),
  );

  useEffect(() => {
    if (!hasAddressForTax) {
      setEstimatedTaxDollars(null);
      return undefined;
    }
    const gen = ++taxEstimateRequestRef.current;
    const timerId = window.setTimeout(async () => {
      if (gen !== taxEstimateRequestRef.current) return;
      const { data } = await estimateSubscriptionTax({
        state: checkoutFields.state.trim(),
        zip: checkoutFields.zip.trim(),
        countryCode: 'US',
      });
      if (gen !== taxEstimateRequestRef.current) return;
      if (typeof data?.taxAmountDollars === 'number') {
        setEstimatedTaxDollars(data.taxAmountDollars);
      }
    }, 600);
    return () => window.clearTimeout(timerId);
  }, [hasAddressForTax, checkoutFields.state, checkoutFields.zip]);

  useEffect(() => {
    if (!canCreateCheckoutSession) {
      elementsIntentRequestGenRef.current += 1;
      setClientSecret(null);
      setCheckoutEnabled(false);
      setIsLoadingClientSecret(false);
      setErrorStripe(null);
      return undefined;
    }

    setIsLoadingClientSecret(true);
    const gen = ++elementsIntentRequestGenRef.current;
    const timerId = window.setTimeout(() => {
      if (gen !== elementsIntentRequestGenRef.current) {
        return;
      }
      fetchClientSecret(products, appliedCouponCode, gen);
    }, ELEMENTS_INTENT_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [
    appliedCouponCode,
    canCreateCheckoutSession,
    fetchClientSecret,
    products,
  ]);

  const getReturnUrl = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(SUBSCRIPTION_FROM_CHECKOUT_PARAM, 'true');
    nextParams.delete('checkout_session_id');
    nextParams.delete(STRIPE_PAYMENT_INTENT_ID_PARAM);
    return `${window.location.origin}${window.location.pathname}?${nextParams.toString()}`;
  }, [searchParams]);

  const handlePaymentSuccess = useCallback(async (paymentIntentId, expressDetails) => {
    const isExpress = Boolean(expressDetails);
    const emailLive = expressDetails?.email
      || checkoutEmailRef.current.trim();
    const fieldsLive = checkoutFieldsRef.current;
    const mappedProducts = mapSubscriptionProductsForApi(products);

    const firstName = expressDetails?.firstName
      || fieldsLive.firstName.trim() || undefined;
    const lastName = expressDetails?.lastName
      || fieldsLive.lastName.trim() || undefined;
    const phone = getPhoneForApi(expressDetails?.phone || fieldsLive.phone);
    const shippingAddress = expressDetails ? {
      address1: expressDetails.address1 || undefined,
      address2: expressDetails.address2 || undefined,
      city: expressDetails.city || undefined,
      provinceCode: expressDetails.state || undefined,
      zip: expressDetails.zip || undefined,
      countryCode: expressDetails.countryCode || 'US',
    } : {
      address1: fieldsLive.address1.trim() || undefined,
      address2: fieldsLive.address2.trim() || undefined,
      city: fieldsLive.city.trim() || undefined,
      provinceCode: fieldsLive.state.trim() || undefined,
      zip: fieldsLive.zip.trim() || undefined,
      countryCode: 'US',
    };

    if (isExpress) {
      console.info('[Trashie][Express Checkout] handlePaymentSuccess — calling finalize API', {
        paymentIntentId,
        email: emailLive ? `${emailLive.slice(0, 3)}***` : '(none)',
        firstName: firstName || '(none)',
        lastName: lastName || '(none)',
        phone: phone ? '(set)' : '(none)',
        shippingAddress: {
          hasAddress1: Boolean(shippingAddress.address1),
          hasCity: Boolean(shippingAddress.city),
          hasProvinceCode: Boolean(shippingAddress.provinceCode),
          hasZip: Boolean(shippingAddress.zip),
          countryCode: shippingAddress.countryCode,
        },
      });
    }

    const { data, error: finalizeError } = await finalizeSubscriptionElementsPayment({
      paymentIntentId,
      products: mappedProducts,
      couponId: appliedCouponCode || undefined,
      email: emailLive || undefined,
      firstName,
      lastName,
      phone,
      shippingAddress,
      marketingOptedIn: emailMarketingOptIn,
      smsMarketingOptedIn: smsMarketingOptIn,
      searchParams,
    });

    if (isExpress) {
      console.info('[Trashie][Express Checkout] handlePaymentSuccess — finalize API returned', {
        paymentIntentId,
        hasData: Boolean(data),
        subscriptionId: data?.subscriptionId || null,
        hasError: Boolean(finalizeError),
        errorMessage: finalizeError?.message,
        errorCode: finalizeError?.code,
      });
    }

    if (finalizeError || !data?.subscriptionId) {
      const displayedError = finalizeError?.message || getGenericError();
      if (isExpress) {
        console.error('[Trashie][Express Checkout] handlePaymentSuccess — FINALIZE FAILED', {
          paymentIntentId,
          displayedError,
          rawFinalizeError: finalizeError,
        });
      }
      setErrorStripe(displayedError);
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(SUBSCRIPTION_FROM_CHECKOUT_PARAM, 'true');
    if (paymentIntentId) {
      nextParams.set(STRIPE_PAYMENT_INTENT_ID_PARAM, paymentIntentId);
    }
    setElementsPaymentResult({
      subscriptionId: data.subscriptionId,
      paymentIntentId,
    });
    history.replace(`${window.location.pathname}?${nextParams.toString()}`);
    setStep(SUBSCRIPTION_STEPS.CONFIRMATION);
  }, [
    appliedCouponCode,
    history,
    products,
    searchParams,
    setStep,
    setElementsPaymentResult,
    emailMarketingOptIn,
    smsMarketingOptIn,
  ]);

  useEffect(() => {
    posthog.capture(getTrackerIdEventName(trackerView));
  }, [posthog]);

  useEffect(() => {
    if (hasTrackedSubscriptionStarted.current) {
      return;
    }
    hasTrackedSubscriptionStarted.current = true;
    pushSubscriptionStartedEvent(userData);
  }, [userData]);

  useEffect(() => {
    if (couponMessage || couponErrorMessage) {
      setCouponSectionOpen(true);
    }
  }, [couponMessage, couponErrorMessage]);

  useEffect(() => {
    const accountEmail = userData?.email || firebaseUser?.email || '';
    if (firebaseUser && accountEmail) {
      setCheckoutEmail(accountEmail);
      return;
    }
    if (!firebaseUser && !checkoutEmail && userData?.email) {
      setCheckoutEmail(userData.email);
    }
  }, [checkoutEmail, firebaseUser, firebaseUser?.email, userData?.email]);

  useEffect(() => {
    if (phoneHydratedFromProfileRef.current) {
      return;
    }
    const raw =
      userData?.phone ||
      userData?.phoneNumber ||
      firebaseUser?.phoneNumber ||
      '';
    const digits = normalizeUsPhoneDigits(String(raw));
    if (!digits) {
      return;
    }
    phoneHydratedFromProfileRef.current = true;
    setCheckoutFields((prev) => ({
      ...prev,
      phone: formatUsPhoneDisplayFromDigits(digits),
    }));
  }, [userData?.phone, userData?.phoneNumber, firebaseUser?.phoneNumber]);

  const isAuthenticatedCheckout = Boolean(firebaseUser);

  useEffect(() => {
    if (firebaseUser) {
      setEmailEligibilityError('');
      return undefined;
    }

    const emailTrimmed = checkoutEmail.trim();
    if (!CHECKOUT_EMAIL_PATTERN.test(emailTrimmed)) {
      setEmailEligibilityError('');
      return undefined;
    }

    let cancelled = false;
    const timerId = window.setTimeout(async () => {
      const { data, error } = await getSubscriptionEmailEligibility(emailTrimmed);
      if (cancelled) {
        return;
      }
      if (error) {
        setEmailEligibilityError('');
        return;
      }
      if (data?.hasActiveSubscription) {
        setEmailEligibilityError(EMAIL_SUBSCRIPTION_CONFLICT_MESSAGE);
      } else {
        setEmailEligibilityError('');
      }
    }, EMAIL_ELIGIBILITY_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [checkoutEmail, firebaseUser]);

  useEffect(() => {
    if (!clientSecret) {
      return;
    }
    const merged = `${checkoutFields.firstName} ${checkoutFields.lastName}`.trim();
    setCardholderName((prev) => prev || merged);
  }, [clientSecret, checkoutFields.firstName, checkoutFields.lastName]);

  const updateCheckoutField = useCallback((field, value) => {
    setCheckoutFields((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  useEffect(() => {
    const inputEl = addressAutocompleteRef.current;
    if (!inputEl) {
      return undefined;
    }

    let autocompleteObj;
    let pollId;
    let cancelled = false;

    const teardown = () => {
      if (autocompleteObj && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteObj);
        autocompleteObj = undefined;
      }
    };

    const sessionTokenHolder = { current: null };

    const attach = () => {
      if (cancelled || !addressAutocompleteRef.current || !window.google?.maps?.places) {
        return false;
      }
      teardown();
      sessionTokenHolder.current = new window.google.maps.places.AutocompleteSessionToken();
      autocompleteObj = new window.google.maps.places.Autocomplete(inputEl, {
        types: ['address'],
        sessionToken: sessionTokenHolder.current,
        componentRestrictions: { country: ['us'] },
      });
      autocompleteObj.setFields(['address_components', 'name']);
      autocompleteObj.addListener('place_changed', () => {
        const place = autocompleteObj.getPlace();
        if (place?.address_components?.length) {
          const parsed = parsePlaceAddress(place);
          setCheckoutFields((prev) => ({
            ...prev,
            address1: parsed.address1 || prev.address1,
            city: parsed.city ?? '',
            state: parsed.stateCode ?? '',
            zip: parsed.zip ?? '',
          }));
        }
        sessionTokenHolder.current = new window.google.maps.places.AutocompleteSessionToken();
        if (typeof autocompleteObj.setOptions === 'function') {
          autocompleteObj.setOptions({ sessionToken: sessionTokenHolder.current });
        }
      });
      return true;
    };

    if (!attach()) {
      let pollAttempts = 0;
      const maxPollAttempts = 100;
      pollId = window.setInterval(() => {
        pollAttempts += 1;
        if (attach() || pollAttempts >= maxPollAttempts) {
          if (pollId != null) {
            window.clearInterval(pollId);
            pollId = undefined;
          }
        }
      }, 100);
    }

    return () => {
      cancelled = true;
      if (pollId != null) {
        window.clearInterval(pollId);
      }
      teardown();
    };
  }, []);

  const summaryProps = {
    membershipDisplayPrice,
    subscriptionInterval,
    isCouponApplied,
    fullValue: membershipDisplayPrice,
    discountedValue,
    estimatedTaxDollars,
  };

  const billingPhoneRaw = useMemo(() => {
    const fromField = getPhoneForApi(checkoutFields.phone);
    if (fromField) {
      return fromField;
    }
    const candidates = [userData?.phone, userData?.phoneNumber, firebaseUser?.phoneNumber];
    for (let i = 0; i < candidates.length; i += 1) {
      const c = candidates[i];
      const api = getPhoneForApi(c);
      if (api) {
        return api;
      }
      if (c != null && String(c).trim() !== '') {
        return String(c).trim();
      }
    }
    return '';
  }, [checkoutFields.phone, userData, firebaseUser]);

  const isCheckoutFormReady = useMemo(() => {
    const email = checkoutEmail.trim();
    if (!email || !CHECKOUT_EMAIL_PATTERN.test(email) || emailEligibilityError) {
      return false;
    }
    const f = checkoutFields;
    if (
      !f.firstName.trim() ||
      !f.lastName.trim() ||
      !f.address1.trim() ||
      !f.city.trim() ||
      !f.state.trim() ||
      !f.zip.trim()
    ) {
      return false;
    }
    const phoneTouched = f.phone.trim().length > 0;
    if (phoneTouched && !isValidUsPhone(f.phone)) {
      return false;
    }
    if (!cardholderName.trim()) {
      return false;
    }
    return true;
  }, [cardholderName, checkoutEmail, checkoutFields, emailEligibilityError]);

  let paymentSectionBody;
  if (clientSecret) {
    paymentSectionBody = (
      <SubscriptionUnlimitedElementsCheckout
        clientSecret={clientSecret}
        disabled={!checkoutEnabled}
        checkoutFormReady={isCheckoutFormReady}
        email={checkoutEmail.trim() || undefined}
        billingCountryCode={billingCountryCode}
        billingPostalCode={checkoutFields.zip}
        billingAddressLine1={checkoutFields.address1}
        billingAddressLine2={checkoutFields.address2}
        billingCity={checkoutFields.city}
        billingState={checkoutFields.state}
        billingPhoneRaw={billingPhoneRaw}
        cardholderName={cardholderName}
        onBillingCountryCodeChange={setBillingCountryCode}
        onBillingPostalCodeChange={(value) => updateCheckoutField('zip', value)}
        onCardholderNameChange={setCardholderName}
        returnUrl={getReturnUrl()}
        onError={(message) => setErrorStripe(message || getGenericError())}
        onSuccess={handlePaymentSuccess}
        expressPortalRef={expressPortalRef}
      />
    );
  } else if (isLoadingClientSecret) {
    paymentSectionBody = (
      <div
        className="SubscriptionUnlimitedCheckout__paymentLoading"
        role="status"
        aria-live="polite"
      >
        <LoadingBar />
      </div>
    );
  } else if (!canCreateCheckoutSession) {
    paymentSectionBody = (
      <p className="SubscriptionUnlimitedCheckout__paymentHint">
        Select at least one item to load payment.
      </p>
    );
  } else {
    paymentSectionBody = (
      <p className="SubscriptionUnlimitedCheckout__paymentHint">
        Payment could not be loaded. Check the message above or refresh the page.
      </p>
    );
  }

  const deliverySection = (
    <div className="SubscriptionUnlimitedCheckout__formPadded SubscriptionUnlimitedCheckout__formPadded--delivery">
      <div className="SubscriptionUnlimitedCheckout__fieldSectionTitle">Contact</div>
      <div className="SubscriptionUnlimitedCheckout__contactEmailGroup">
        <div className="SubscriptionUnlimitedCheckout__field">
          <span className="SubscriptionUnlimitedCheckout__fieldLabel">Email</span>
          <input
            id="unlimited-email"
            type="email"
            autoComplete="email"
            readOnly={isAuthenticatedCheckout}
            aria-readonly={isAuthenticatedCheckout ? 'true' : undefined}
            aria-invalid={emailEligibilityError ? 'true' : undefined}
            aria-describedby={emailEligibilityError ? 'unlimited-email-error' : undefined}
            className={classNames('SubscriptionUnlimitedCheckout__textFieldInput', {
              'SubscriptionUnlimitedCheckout__textFieldInput--error': Boolean(emailEligibilityError),
              'SubscriptionUnlimitedCheckout__textFieldInput--readOnly': isAuthenticatedCheckout,
            })}
            placeholder="Email"
            value={checkoutEmail}
            onChange={(event) => {
              if (!isAuthenticatedCheckout) {
                setCheckoutEmail(event.target.value);
              }
            }}
          />
          {emailEligibilityError ? (
            <p
              id="unlimited-email-error"
              className="SubscriptionUnlimitedCheckout__fieldError"
              role="alert"
            >
              {emailEligibilityError}
            </p>
          ) : null}
        </div>

        <label
          className={classNames(
            'SubscriptionUnlimitedCheckout__marketingOptIn',
            'SubscriptionUnlimitedCheckout__marketingOptIn--emailOffers',
          )}
          htmlFor="unlimited-email-marketing-opt-in"
        >
          <input
            id="unlimited-email-marketing-opt-in"
            type="checkbox"
            checked={emailMarketingOptIn}
            onChange={(event) => setEmailMarketingOptIn(event.target.checked)}
          />
          <span>Email me with news and offers</span>
        </label>
      </div>

      <div className="SubscriptionUnlimitedCheckout__fieldSectionTitle">Delivery</div>
      <div className="SubscriptionUnlimitedCheckout__contactGrid">
        <div className="SubscriptionUnlimitedCheckout__field">
          <span className="SubscriptionUnlimitedCheckout__fieldLabel">First name</span>
          <input
            id="unlimited-first-name"
            type="text"
            autoComplete="given-name"
            className="SubscriptionUnlimitedCheckout__textFieldInput"
            value={checkoutFields.firstName}
            onChange={(event) => updateCheckoutField('firstName', event.target.value)}
          />
        </div>
        <div className="SubscriptionUnlimitedCheckout__field">
          <span className="SubscriptionUnlimitedCheckout__fieldLabel">Last name</span>
          <input
            id="unlimited-last-name"
            type="text"
            autoComplete="family-name"
            className="SubscriptionUnlimitedCheckout__textFieldInput"
            value={checkoutFields.lastName}
            onChange={(event) => updateCheckoutField('lastName', event.target.value)}
          />
        </div>
        <div className="SubscriptionUnlimitedCheckout__field SubscriptionUnlimitedCheckout__field--full">
          <span className="SubscriptionUnlimitedCheckout__fieldLabel">Address</span>
          <input
            ref={addressAutocompleteRef}
            id="unlimited-address1"
            type="text"
            autoComplete="address-line1"
            className="SubscriptionUnlimitedCheckout__textFieldInput"
            placeholder="Start typing your address"
            value={checkoutFields.address1}
            onChange={(event) => updateCheckoutField('address1', event.target.value)}
          />
        </div>
        <div className="SubscriptionUnlimitedCheckout__field SubscriptionUnlimitedCheckout__field--full">
          <span className="SubscriptionUnlimitedCheckout__fieldLabel">Apt / Unit</span>
          <input
            id="unlimited-address2"
            type="text"
            autoComplete="address-line2"
            className="SubscriptionUnlimitedCheckout__textFieldInput"
            placeholder="Apt / Unit (optional)"
            value={checkoutFields.address2}
            onChange={(event) => updateCheckoutField('address2', event.target.value)}
          />
        </div>
        <div className="SubscriptionUnlimitedCheckout__cityStateZipRow">
          <div className="SubscriptionUnlimitedCheckout__field">
            <span className="SubscriptionUnlimitedCheckout__fieldLabel">City</span>
            <input
              id="unlimited-city"
              type="text"
              autoComplete="address-level2"
              className="SubscriptionUnlimitedCheckout__textFieldInput"
              value={checkoutFields.city}
              onChange={(event) => updateCheckoutField('city', event.target.value)}
            />
          </div>
          <div className="SubscriptionUnlimitedCheckout__field">
            <span className="SubscriptionUnlimitedCheckout__fieldLabel">State</span>
            <select
              id="unlimited-state"
              className={classNames(
                'SubscriptionUnlimitedCheckout__textFieldInput',
                'SubscriptionUnlimitedCheckout__stateSelect',
                {
                  'SubscriptionUnlimitedCheckout__stateSelect--empty':
                    checkoutFields.state === '',
                },
              )}
              autoComplete="address-level1"
              value={checkoutFields.state}
              onChange={(event) => updateCheckoutField('state', event.target.value)}
            >
              <option value="">Select state</option>
              {usStatesWithCodes.map(({ code, name }) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <div className="SubscriptionUnlimitedCheckout__field">
            <span className="SubscriptionUnlimitedCheckout__fieldLabel">Zip Code</span>
            <input
              id="unlimited-zip"
              type="text"
              autoComplete="postal-code"
              className="SubscriptionUnlimitedCheckout__textFieldInput"
              value={checkoutFields.zip}
              onChange={(event) => updateCheckoutField('zip', event.target.value)}
            />
          </div>
        </div>
        <div className="SubscriptionUnlimitedCheckout__postDeliveryContact">
          <div className="SubscriptionUnlimitedCheckout__field">
            <span className="SubscriptionUnlimitedCheckout__fieldLabel">Phone</span>
            <input
              id="unlimited-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              className={classNames('SubscriptionUnlimitedCheckout__textFieldInput', {
                'SubscriptionUnlimitedCheckout__textFieldInput--error':
                  checkoutFields.phone.trim().length > 0 && !isValidUsPhone(checkoutFields.phone),
              })}
              placeholder="+1 (XXX) XXX-XXXX"
              value={checkoutFields.phone}
              onChange={(event) => {
                const rawDigits = event.target.value.replace(/\D/g, '');
                let d = rawDigits;
                if (d.startsWith('1') && d.length > 1) {
                  d = d.slice(1);
                }
                updateCheckoutField('phone', formatUsPhoneDisplayFromDigits(d.slice(0, 10)));
              }}
            />
            {checkoutFields.phone.trim().length > 0 && !isValidUsPhone(checkoutFields.phone) ? (
              <p className="SubscriptionUnlimitedCheckout__fieldError" role="alert">
                Enter a valid US phone number or clear this field.
              </p>
            ) : null}
          </div>

          <label
            className="SubscriptionUnlimitedCheckout__marketingOptIn SubscriptionUnlimitedCheckout__marketingOptIn--sms"
            htmlFor="unlimited-sms-marketing-opt-in"
          >
            <input
              id="unlimited-sms-marketing-opt-in"
              type="checkbox"
              checked={smsMarketingOptIn}
              onChange={(event) => setSmsMarketingOptIn(event.target.checked)}
            />
            <span>Text me with news and offers</span>
          </label>
        </div>
      </div>

      <div className="SubscriptionUnlimitedCheckout__section SubscriptionUnlimitedCheckout__section--coupon">
        <button
          type="button"
          className="SubscriptionUnlimitedCheckout__couponToggle"
          onClick={() => setCouponSectionOpen((open) => !open)}
          aria-expanded={couponSectionOpen}
        >
          <CouponTagIcon />
          <span>
            {couponSectionOpen ? 'Hide coupon code' : 'Add coupon code'}
          </span>
        </button>
        {couponSectionOpen && (
          <div className="SubscriptionUnlimitedCheckout__couponFields">
            <CouponCodeInput
              value={couponCode}
              onChange={setCouponCode}
              onApply={() => setAppliedCouponCode(couponCode)}
              disabled={isLoadingClientSecret}
              applyDisabled={!couponCode || isLoadingClientSecret}
              successMessage={couponMessage}
              errorMessage={couponErrorMessage}
              designSystem
            />
          </div>
        )}
        {errorStripe && (
          <div className="SubscriptionUnlimitedCheckout__stripeError">{errorStripe}</div>
        )}
      </div>
    </div>
  );

  const stripeSection = (
    <div className="SubscriptionUnlimitedCheckout__section SubscriptionUnlimitedCheckout__section--stripe">
      {paymentSectionBody}
    </div>
  );

  const termsSection = info ? (
    <div className="SubscriptionUnlimitedCheckout__formPadded SubscriptionUnlimitedCheckout__formPadded--terms">
      <div className="SubscriptionUnlimitedCheckout__terms">
        <Markdown components={subscriptionUnlimitedCheckoutTermsMarkdownComponents}>
          {info}
        </Markdown>
      </div>
    </div>
  ) : null;

  return (
    <div className="SubscriptionUnlimitedCheckout">
      <div className="SubscriptionUnlimitedCheckout__topFixed">
        <header className="SubscriptionUnlimitedCheckout__headerBar">
          <Link className="SubscriptionUnlimitedCheckout__logoLink" to={ROUTE_HOME}>
            <img
              className="SubscriptionUnlimitedCheckout__logo"
              src={isNarrowViewport ? mobileNavbarLogo : trashieLogo}
              alt="Trashie"
            />
          </Link>
        </header>
      </div>

      <div className="SubscriptionUnlimitedCheckout__shell">
        {isNarrowViewport && (
          <div className="SubscriptionUnlimitedCheckout__mobileSummary">
            <button
              type="button"
              className="SubscriptionUnlimitedCheckout__mobileSummaryToggle"
              onClick={() => setSummaryExpanded((v) => !v)}
              aria-expanded={summaryExpanded}
            >
              <span className="SubscriptionUnlimitedCheckout__mobileSummaryLead">
                <span className="SubscriptionUnlimitedCheckout__mobileSummaryBadge" aria-hidden>
                  <img
                    src={unlimitedBadgeFigmaIcon}
                    alt=""
                    className="SubscriptionUnlimitedCheckout__mobileSummaryBadgeIcon"
                  />
                </span>
                <span className="SubscriptionUnlimitedCheckout__mobileSummaryTitleRow">
                  <span className="SubscriptionUnlimitedCheckout__mobileSummaryMembershipLabel">
                    Unlimited Membership
                  </span>
                  <span className="SubscriptionUnlimitedCheckout__mobileSummaryPrice">
                    {formatCurrencyNumber(
                      isCouponApplied && discountedValue != null
                        ? discountedValue
                        : membershipDisplayPrice,
                    )}
                  </span>
                </span>
              </span>
              <img
                src={mobileSummaryChevronIcon}
                alt=""
                aria-hidden
                className={classNames('SubscriptionUnlimitedCheckout__mobileSummaryChevron', {
                  'SubscriptionUnlimitedCheckout__mobileSummaryChevron--expanded': summaryExpanded,
                })}
              />
            </button>
            <div
              className={classNames('SubscriptionUnlimitedCheckout__mobileSummaryCollapse', {
                'SubscriptionUnlimitedCheckout__mobileSummaryCollapse--expanded': summaryExpanded,
              })}
              aria-hidden={!summaryExpanded}
            >
              <div className="SubscriptionUnlimitedCheckout__mobileSummaryCollapseInner">
                <div className="SubscriptionUnlimitedCheckout__mobileSummaryBody">
                  <SubscriptionOrderSummary {...summaryProps} hideLegal hideCardTitle />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="SubscriptionUnlimitedCheckout__grid">
          <div className="SubscriptionUnlimitedCheckout__main">
            <div className="SubscriptionUnlimitedCheckout__form">
              <div ref={expressPortalRef} />
              <div className="SubscriptionUnlimitedCheckout__checkoutDetails">
                <button
                  type="button"
                  id="unlimited-checkout-details-toggle"
                  className="SubscriptionUnlimitedCheckout__checkoutDetailsToggle"
                  onClick={() => setCheckoutFormExpanded((v) => !v)}
                  aria-expanded={checkoutFormExpanded}
                  aria-controls="unlimited-checkout-details-panel"
                >
                  <span className="SubscriptionUnlimitedCheckout__checkoutDetailsToggleLabel">
                    Pay by Card
                  </span>
                  <img
                    src={mobileSummaryChevronIcon}
                    alt=""
                    aria-hidden
                    className={classNames(
                      'SubscriptionUnlimitedCheckout__checkoutDetailsChevron',
                      {
                        'SubscriptionUnlimitedCheckout__checkoutDetailsChevron--expanded':
                          checkoutFormExpanded,
                      },
                    )}
                  />
                </button>
                <div
                  id="unlimited-checkout-details-panel"
                  role="region"
                  aria-labelledby="unlimited-checkout-details-toggle"
                  aria-hidden={!checkoutFormExpanded}
                  className={classNames('SubscriptionUnlimitedCheckout__checkoutDetailsCollapse', {
                    'SubscriptionUnlimitedCheckout__checkoutDetailsCollapse--expanded':
                      checkoutFormExpanded,
                  })}
                >
                  <div className="SubscriptionUnlimitedCheckout__checkoutDetailsCollapseInner">
                    <div className="SubscriptionUnlimitedCheckout__checkoutDetailsPanel">
                      {deliverySection}
                      {stripeSection}
                    </div>
                  </div>
                </div>
              </div>
              {termsSection}
            </div>
          </div>

          {!isNarrowViewport && (
            <>
              <div className="SubscriptionUnlimitedCheckout__gridDivider" aria-hidden="true" />
              <SubscriptionOrderSummary
                className="SubscriptionUnlimitedCheckout__aside"
                {...summaryProps}
              />
            </>
          )}
        </div>

        {isNarrowViewport && (
          <div className="SubscriptionUnlimitedCheckout__mobileLegalFooter">
            <SubscriptionOrderSummaryLegal />
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionUnlimitedCheckout;
